import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import axios from 'axios';
import WorkOrderSearchPicker from '../../shared/WorkOrderSearchPicker';
import CustomerAutocomplete from '../../shared/CustomerAutocomplete';
import NewCustomerForm, { EMPTY_NEW_CUSTOMER, isNewCustomerValid } from '../../shared/NewCustomerForm';
import LahetePicker from './LahetePicker';
import { COMPACT_INPUT_SX, FOCUS_RING_SX, Overline, SEGMENT_SX, destinationText } from './builderUi';

const DIALOG_PAPER_SX = (fullScreen) => ({ sx: { borderRadius: fullScreen ? 0 : '16px', ...FOCUS_RING_SX } });
const DIALOG_TITLE_SX = { fontSize: 17, fontWeight: 700, letterSpacing: '-.2px', pb: 0.5 };
const DIALOG_ACTIONS_SX = { px: 3, pb: 2.5, pt: 1, gap: 1 };
const BUTTON_SX = { textTransform: 'none', fontWeight: 600, boxShadow: 'none' };

/**
 * The destination choice (Varasto / Työmääräys / Lähete / Uusi lähete) used both as the
 * cart's default for new rows and per row. Options are gated by the builder context
 * (inventory tracking, parts sales, the caller's permissions).
 */
export function destinationOptions(t, context) {
  const allowed = context?.default_destination_allowed || {};
  const options = [
    {
      kind: 'stock',
      label: context?.inventory_tracking_enabled ? t('orderBuilder.dest.stock') : t('orderBuilder.dest.noDestination'),
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
      enabled: allowed.stock !== false,
    },
    { kind: 'maintenance', label: t('orderBuilder.dest.workOrder'), icon: <BuildOutlinedIcon sx={{ fontSize: 16 }} />, enabled: !!allowed.maintenance },
    { kind: 'parts_sale', label: t('orderBuilder.dest.lahete'), icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />, enabled: !!allowed.parts_sale },
    { kind: 'new_parts_sale', label: t('orderBuilder.dest.newLahete'), icon: <AddIcon sx={{ fontSize: 16 }} />, enabled: !!allowed.new_parts_sale },
  ];
  return options.filter((o) => o.enabled);
}

/** Segmented default-destination control with its inline pickers. */
export function DefaultDestinationBar({ context, value, onChange, newLahete, onNewLaheteChange }) {
  const { t } = useTranslation();
  const options = destinationOptions(t, context);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  const pick = (kind) => {
    if (!kind) return;
    if (kind === 'new_parts_sale' && !newLahete?.customer) setCustomerDialogOpen(true);
    onChange({ kind });
  };

  return (
    <Box data-testid="default-destination">
      <Overline sx={{ mb: 0.75 }}>{t('orderBuilder.defaultDestination')}</Overline>
      <ToggleButtonGroup exclusive size="small" value={value?.kind || 'stock'} onChange={(_, kind) => pick(kind)} sx={SEGMENT_SX}>
        {options.map((o) => (
          <ToggleButton key={o.kind} value={o.kind} data-testid={`default-dest-${o.kind}`}>
            {o.icon}
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {value?.kind === 'maintenance' && (
        <WorkOrderSearchPicker
          value={value.maintenance || null}
          onChange={(wo) => onChange({ kind: 'maintenance', maintenance_id: wo?.id || null, maintenance: wo || null })}
          sx={{ mt: 1 }}
          testId="default-wo-picker"
        />
      )}
      {value?.kind === 'parts_sale' && (
        <LahetePicker
          value={value.parts_sale || null}
          onChange={(sale) => onChange({ kind: 'parts_sale', parts_sale_id: sale?.id || null, parts_sale: sale || null })}
          sx={{ mt: 1 }}
          testId="default-lahete-picker"
        />
      )}
      {value?.kind === 'new_parts_sale' && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <PersonOutlineIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 500 }} data-testid="new-lahete-customer">
            {destinationText(t, { kind: 'new_parts_sale' }, { newLahete })}
          </Typography>
          <Button size="small" onClick={() => setCustomerDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, px: 0.75 }} data-testid="new-lahete-change-customer">
            {newLahete?.customer ? t('orderBuilder.changeCustomer') : t('orderBuilder.chooseCustomer')}
          </Button>
        </Box>
      )}
      <NewLaheteCustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        value={newLahete}
        onChange={onNewLaheteChange}
      />
    </Box>
  );
}

/** Pick (or register) the customer of the cart's one new lähete. */
export function NewLaheteCustomerDialog({ open, onClose, value, onChange }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [customer, setCustomer] = useState(value?.customer || null);
  const [draft, setDraft] = useState({ ...EMPTY_NEW_CUSTOMER, type: 'company' });
  const [notes, setNotes] = useState(value?.notes || '');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const canConfirm = tab === 0 ? !!customer : isNewCustomerValid(draft, { requireName: true });

  const confirm = async () => {
    let chosen = customer;
    if (tab === 1) {
      setCreating(true);
      setError(null);
      try {
        const created = await axios.post('/api/customers/', draft);
        chosen = created.data;
      } catch (e) {
        setError(t('Failed to create customer'));
        setCreating(false);
        return;
      }
      setCreating(false);
    }
    onChange({ customer: chosen, notes });
    onClose();
  };

  return (
    <Dialog open={open} onClose={() => !creating && onClose()} maxWidth={tab === 1 ? 'sm' : 'xs'} fullWidth PaperProps={DIALOG_PAPER_SX(false)}>
      <DialogTitle sx={DIALOG_TITLE_SX}>{t('orderBuilder.newLaheteDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.45 }}>
          {t('orderBuilder.newLaheteDialog.help')}
        </Typography>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="fullWidth"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 } }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label={t('Select existing')} data-testid="new-lahete-tab-existing" />
          <Tab icon={<AddIcon />} iconPosition="start" label={t('Create new')} data-testid="new-lahete-tab-new" />
        </Tabs>
        {tab === 0 ? (
          <CustomerAutocomplete value={customer} onChange={setCustomer} autoFocus placeholder={t('parts_sales.selectCustomer')} />
        ) : (
          <NewCustomerForm t={t} value={draft} onChange={setDraft} size="small" requireName />
        )}
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label={t('orderBuilder.newLaheteDialog.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={DIALOG_ACTIONS_SX}>
        <Button onClick={onClose} disabled={creating} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {t('Cancel')}
        </Button>
        <Button variant="contained" disabled={!canConfirm || creating} onClick={confirm} data-testid="new-lahete-confirm" sx={BUTTON_SX}>
          {t('orderBuilder.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Per-line destination select with inline pickers in a small dialog. */
export function LineDestinationSelect({ context, line, onChange, newLahete, onNewLaheteChange }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const options = destinationOptions(t, context);
  const [pickerKind, setPickerKind] = useState(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [draftPick, setDraftPick] = useState(null);

  const select = (kind) => {
    if (kind === 'maintenance' || kind === 'parts_sale') {
      setDraftPick(null);
      setPickerKind(kind);
      return;
    }
    if (kind === 'new_parts_sale' && !newLahete?.customer) setCustomerDialogOpen(true);
    onChange({ kind });
  };

  const confirmPick = () => {
    if (pickerKind === 'maintenance') {
      onChange({ kind: 'maintenance', maintenance_id: draftPick?.id || null, maintenance: draftPick || null });
    } else {
      onChange({ kind: 'parts_sale', parts_sale_id: draftPick?.id || null, parts_sale: draftPick || null });
    }
    setPickerKind(null);
  };

  const dest = line.destination || { kind: 'stock' };
  const current = dest.kind || 'stock';
  const hasSpecificTarget = (dest.kind === 'maintenance' && dest.maintenance_id) || (dest.kind === 'parts_sale' && dest.parts_sale_id);

  const currentOption = options.find((o) => o.kind === current);

  return (
    <>
      <TextField
        select
        size="small"
        value={current}
        onChange={(e) => select(e.target.value)}
        sx={{ minWidth: 170, maxWidth: '100%', ...COMPACT_INPUT_SX, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0.75, py: 0.75 } }}
        data-testid={`line-dest-field-${line.key}`}
        SelectProps={{
          renderValue: () => (
            <>
              <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', flexShrink: 0 }}>{currentOption?.icon}</Box>
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }} noWrap data-testid={`line-dest-${line.key}`}>
                {destinationText(t, dest, { newLahete, inventoryEnabled: context?.inventory_tracking_enabled })}
              </Typography>
            </>
          ),
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.kind} value={o.kind} sx={{ fontSize: 13.5, gap: 1, '& .MuiSvgIcon-root': { color: 'text.secondary' } }}>
            {o.icon}
            {o.label}
            {o.kind === current && hasSpecificTarget ? ` · ${t('orderBuilder.change')}` : ''}
          </MenuItem>
        ))}
      </TextField>
      <Dialog open={!!pickerKind} onClose={() => setPickerKind(null)} fullWidth maxWidth="sm" fullScreen={fullScreen} PaperProps={DIALOG_PAPER_SX(fullScreen)}>
        <DialogTitle sx={DIALOG_TITLE_SX}>{pickerKind === 'maintenance' ? t('orderBuilder.dest.pickWorkOrder') : t('orderBuilder.dest.pickLahete')}</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {pickerKind === 'maintenance' ? (
            <WorkOrderSearchPicker value={draftPick} onChange={setDraftPick} autoFocus testId="line-wo-picker" />
          ) : (
            <LahetePicker value={draftPick} onChange={setDraftPick} autoFocus testId="line-lahete-picker" />
          )}
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button onClick={() => setPickerKind(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" disabled={!draftPick} onClick={confirmPick} data-testid="line-dest-confirm" sx={BUTTON_SX}>
            {t('orderBuilder.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <NewLaheteCustomerDialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} value={newLahete} onChange={onNewLaheteChange} />
    </>
  );
}
